/**
 * History direction tracker.
 *
 * Sets `visit.direction` to 'forward' or 'backward' so the transition layer can play a
 * navigation in reverse when the user goes back. Read by
 * `resources/js/transitions/base.js` and by the `--reverse` suffix in router/swup.js.
 *
 * WHAT THIS USED TO DO, AND WHY IT NEVER WORKED
 *
 * It maintained its own monotonic `psdIndex`, stamping each history entry and comparing
 * indices on popstate to infer direction. The stamp was written like this:
 *
 *     visit.to.state = visit.to.state || {};
 *     visit.to.state.psdIndex = currentIndex;
 *
 * `visit.to.state` is a plain object hanging off the visit. Swup never reads it back when
 * it calls `history.pushState()`, so the index went nowhere: every forward navigation
 * pushed a fresh Swup state and the psdIndex vanished with it. On the way back,
 * `visit.to.state?.psdIndex` was therefore `undefined`, the comparison fell through to the
 * else branch, and EVERY visit — including every back-button press on all 22 pages — was
 * reported as 'forward'. `--reverse` was never appended and no reverse transition has
 * ever played on this site.
 *
 * It failed silently in both directions: nothing threw, `data-transition` still carried a
 * valid name, and the animation still ran. It just always ran forwards. The three e2e
 * specs that would have caught it had been pointing at `/work`, a page this fork deleted,
 * so they died on a 30s timeout before reaching the assertion.
 *
 * WHAT IT DOES NOW
 *
 * Swup already tracks this, and tracks it correctly, because its own history index IS
 * persisted into the entry it pushes. `visit.history` (Swup 4.9.2) carries:
 *
 *     popstate   boolean — this visit came from a browser history navigation
 *     direction  'backwards' | 'forwards' | undefined — only set when popstate is true
 *
 * So there is nothing to compute and no parallel state to keep in step. Verified against
 * the running site: a link click reports `popstate: false`; `history.back()` reports
 * `popstate: true, direction: 'backwards'`; `history.forward()` reports
 * `popstate: true, direction: 'forwards'`.
 *
 * NOTE THE PLURAL. Swup says 'backwards'/'forwards'; this layer's vocabulary — and
 * `transitions/base.js`, and the `--reverse` check in swup.js — is 'backward'/'forward'.
 * The mapping is deliberate and lives here so exactly one file has to know both
 * spellings.
 *
 * `initHistory()` is gone with the index it existed to seed. Its call site in
 * createRouter() went with it.
 */

/** This layer's vocabulary. Not Swup's — see the note above. */
const BACKWARD = 'backward';
const FORWARD = 'forward';

/**
 * Set `visit.direction` from Swup's own history bookkeeping.
 *
 * Runs on `visit:start`, before the swap, because the transition name is resolved (and
 * the `--reverse` suffix applied) at `before('content:replace')`.
 *
 * @param {import('swup').Visit} visit
 */
export function trackDirection(visit) {
    const isBackward = Boolean(visit.history?.popstate) && visit.history.direction === 'backwards';

    visit.direction = isBackward ? BACKWARD : FORWARD;

    // Applied again in swup.js after resolveTransition, which overwrites
    // `animation.name`. This early pass only matters to anything reading the name
    // between visit:start and content:replace.
    if (isBackward && visit.animation?.name && !visit.animation.name.endsWith('--reverse')) {
        visit.animation.name += '--reverse';
    }
}
